import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import {
  Users,
  Mail,
  BarChart3,
  Settings,
  Play,
  Pause,
  Eye,
  ArrowRight,
  Clock,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Filter,
  RefreshCw,
  Download,
  Send,
  Target,
  Activity,
  MessageCircle,
  Globe,
  Calendar,
  Zap,
  Bot,
  Gift,
  Trophy,
  Star,
  Share2,
  Loader2,
  Trash2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { formatDistanceToNow } from "date-fns";
import { formatDatePretty } from "@/lib/dateUtils";
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

// Types
interface JourneyOverview {
  campaignId: number;
  campaignName: string;
  campaignType: string;
  campaignStatus: "active" | "paused" | "draft" | "completed" | "cancelled";
  totalJourneys: number;
  activeJourneys: number;
  completedJourneys: number;
  pausedJourneys: number;
  cancelledJourneys: number;
}

interface OverallPerformance {
  totalSent: number;
  totalDelivered: number;
  totalOpened: number;
  totalClicked: number;
  openRate: number;
  clickRate: number;
  deliveryRate: number;
}

interface CampaignEmailPerformance {
  campaignId: number;
  campaignName: string;
  totalJourneys: number;
  completedJourneys: number;
  activeJourneys: number;
  emailsSent: number;
  emailsDelivered: number;
  emailsOpened: number;
  emailsClicked: number;
  deliveryRate: number;
  openRate: number;
  clickRate: number;
  clickThroughRate: number;
  conversionRate: number;
  averageJourneyDuration: number;
}

interface JourneyAnalytics {
  journeyStats: JourneyOverview[];
  overallPerformance: OverallPerformance;
}

interface UserJourney {
  id: number;
  userId: number;
  campaignId: number;
  state: string;
  currentStepIndex: number;
  context?: any;
  nextStepAt?: string;
  startedAt: string;
  lastEventAt?: string;
  completedAt?: string;
  pausedAt?: string;
  cancelledAt?: string;
  cancelReason?: string;
  campaignName?: string;
  userEmail?: string;
  userName?: string;
}

interface WeeklyTriggerPayload {
  competitionId: number;
  userIds?: number[];
}

interface WeeklyTriggerResult {
  success?: boolean;
  message?: string;
  emittedCount?: number;
  failedCount?: number;
  data?: {
    competitionId?: number;
    competitionTitle?: string;
    emittedCount?: number;
    failedCount?: number;
    results?: Array<{ userId: number; emitted: boolean; reason?: string }>;
  };
}

const JOURNEY_COLORS = {
  user_registered: "#3B82F6", // Blue for onboarding
  competition_entered: "#10B981", // Green for weekly competition
  share_verified: "#F59E0B", // Amber for referral & leaderboard
  profile_tier_completed: "#8B5CF6", // Purple
  segment_assigned: "#EF4444", // Red
  manual: "#6B7280", // Gray
};

const JOURNEY_ICONS = {
  user_registered: Bot,
  competition_entered: Trophy,
  share_verified: Share2,
  profile_tier_completed: Star,
  segment_assigned: Target,
  manual: Settings,
};

const JOURNEY_NAMES = {
  user_registered: "Onboarding Journey",
  competition_entered: "Weekly Competition",
  share_verified: "Referral & Leaderboard",
  profile_tier_completed: "Profile Completion",
  segment_assigned: "Segment Targeting",
  manual: "Manual Campaign",
};

export default function CrmJourneys() {
  const { toast } = useToast();
  const [selectedJourney, setSelectedJourney] = useState<string>("all");
  const [selectedState, setSelectedState] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [manualCompetitionId, setManualCompetitionId] = useState("");
  const [manualUserIds, setManualUserIds] = useState("");

  // Fetch journey analytics overview
  const {
    data: analyticsData,
    isLoading: analyticsLoading,
    refetch: refetchAnalytics,
  } = useQuery({
    queryKey: ["/api/admin/crm/journeys/analytics/overview"],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        "/api/admin/crm/journeys/analytics/overview"
      );
      return response.json() as Promise<JourneyAnalytics>;
    },
  });

  // Fetch individual journeys
  const {
    data: journeysData,
    isLoading: journeysLoading,
    refetch: refetchJourneys,
  } = useQuery({
    queryKey: [
      "/api/admin/crm/journeys",
      currentPage,
      selectedJourney,
      selectedState,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: "20",
      });

      if (selectedJourney !== "all") {
        params.append("campaignId", selectedJourney);
      }
      if (selectedState !== "all") {
        params.append("state", selectedState);
      }

      const response = await apiRequest(
        "GET",
        `/api/admin/crm/journeys?${params}`
      );
      return response.json();
    },
  });

  // Fetch email performance for a specific journey campaign (when selected)
  const { data: campaignAnalytics } = useQuery<CampaignEmailPerformance | null>(
    {
      queryKey: ["/api/admin/crm/analytics/campaigns", selectedJourney],
      queryFn: async () => {
        if (selectedJourney === "all") return null;
        const response = await apiRequest(
          "GET",
          `/api/admin/crm/analytics/campaigns/${selectedJourney}`
        );
        return response.json();
      },
      enabled: selectedJourney !== "all",
    }
  );

  // Fetch campaigns for dropdown
  const { data: campaignsData } = useQuery({
    queryKey: ["/api/admin/crm/campaigns"],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        "/api/admin/crm/campaigns?limit=100"
      );
      return response.json();
    },
  });

  // Toggle campaign mutation
  const toggleCampaignMutation = useMutation({
    mutationFn: async (campaignId: number) => {
      const response = await apiRequest(
        "POST",
        `/api/admin/crm/campaigns/${campaignId}/toggle`
      );
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Campaign updated",
        description: data.message,
      });
      refetchAnalytics();
      refetchJourneys();
    },
    onError: (error: any) => {
      toast({
        title: "Error updating campaign",
        description: error.message || "Failed to toggle campaign status",
        variant: "destructive",
      });
    },
  });

  const deleteJourneyMutation = useMutation({
    mutationFn: async (journeyId: number) => {
      const response = await apiRequest(
        "DELETE",
        `/api/admin/crm/journeys/${journeyId}/delete`
      );
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Journey deleted",
        description: data?.message || "User journey removed successfully",
      });
      refetchJourneys();
      refetchAnalytics();
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting journey",
        description: error.message || "Failed to delete the journey",
        variant: "destructive",
      });
    },
  });

  const handleDeleteJourney = (journey: UserJourney) => {
    if (deleteJourneyMutation.isPending) {
      return;
    }

    const confirmed = window.confirm(
      `Permanently delete the journey for ${
        journey.userName || journey.userEmail || `User ${journey.userId}`
      }? This cannot be undone.`
    );

    if (!confirmed) return;

    deleteJourneyMutation.mutate(journey.id);
  };

  const triggerWeeklyCompetitionMutation = useMutation<
    WeeklyTriggerResult,
    Error,
    WeeklyTriggerPayload
  >({
    mutationFn: async (payload) => {
      const response = await apiRequest(
        "POST",
        "/api/admin/crm/test/trigger-weekly-competition",
        {
          body: payload,
        }
      );
      return response.json();
    },
    onSuccess: (data) => {
      const emitted = data?.data?.emittedCount ?? data?.emittedCount ?? 0;
      const failed = data?.data?.failedCount ?? data?.failedCount ?? 0;
      toast({
        title: "Weekly journey triggered",
        description:
          failed > 0
            ? `Triggered ${emitted} user(s) with ${failed} failure(s).`
            : `Triggered successfully for ${emitted} user(s).`,
      });
      setManualUserIds("");
      refetchJourneys();
      refetchAnalytics();
    },
    onError: (error) => {
      toast({
        title: "Trigger failed",
        description: error.message || "Unable to trigger weekly journey.",
        variant: "destructive",
      });
    },
  });

  const journeyStats = analyticsData?.journeyStats || [];
  const globalPerformance = analyticsData?.overallPerformance;
  const journeys = journeysData?.journeys || [];
  const allCampaigns = campaignsData?.campaigns || [];
  const pagination = journeysData?.pagination || {};

  // Only show campaigns that actually have journey activity
  const journeyCampaignIds = new Set(journeyStats.map((j) => j.campaignId));
  const campaigns = allCampaigns.filter((campaign: any) =>
    journeyCampaignIds.has(campaign.id)
  );

  // Select performance source: specific journey campaign when selected, otherwise global
  const performance =
    selectedJourney !== "all" && campaignAnalytics
      ? {
          totalSent: campaignAnalytics.emailsSent || 0,
          deliveryRate: campaignAnalytics.deliveryRate || 0,
          openRate: campaignAnalytics.openRate || 0,
          clickRate: campaignAnalytics.clickRate || 0,
        }
      : globalPerformance || {
          totalSent: 0,
          deliveryRate: 0,
          openRate: 0,
          clickRate: 0,
        };

  const handleTriggerWeeklyCompetition = () => {
    if (triggerWeeklyCompetitionMutation.isPending) {
      return;
    }

    if (!manualCompetitionId.trim()) {
      toast({
        title: "Competition ID required",
        description: "Enter the weekly competition ID you want to trigger.",
        variant: "destructive",
      });
      return;
    }

    const competitionId = Number(manualCompetitionId.trim());
    if (Number.isNaN(competitionId)) {
      toast({
        title: "Invalid competition ID",
        description: "Competition ID must be a number.",
        variant: "destructive",
      });
      return;
    }

    let userIds: number[] | undefined;
    if (manualUserIds.trim()) {
      const parsed = manualUserIds
        .split(/[\s,]+/)
        .map((value) => value.trim())
        .filter(Boolean)
        .map((value) => Number(value))
        .filter((value) => !Number.isNaN(value));

      if (parsed.length === 0) {
        toast({
          title: "Invalid user IDs",
          description:
            "Provide numeric user IDs separated by commas or spaces.",
          variant: "destructive",
        });
        return;
      }

      userIds = parsed;
    }

    triggerWeeklyCompetitionMutation.mutate({
      competitionId,
      ...(userIds && userIds.length ? { userIds } : {}),
    });
  };

  const ManualJourneyTools = () => {
    const summary = triggerWeeklyCompetitionMutation.data;
    const summaryData = summary?.data ?? summary;

    return (
      <Card className="mb-8" data-testid="card-manual-journey-tools">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Journey Test Tools
          </CardTitle>
          <CardDescription>
            Manually emit weekly competition events to validate journey delivery
            end-to-end.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="manual-competition-id">
                Weekly competition ID
              </Label>
              <Input
                id="manual-competition-id"
                data-testid="input-weekly-competition-id"
                value={manualCompetitionId}
                onChange={(event) => setManualCompetitionId(event.target.value)}
                placeholder="e.g. 210"
              />
            </div>
            <div>
              <Label htmlFor="manual-user-ids">
                Target user IDs (optional)
              </Label>
              <Input
                id="manual-user-ids"
                data-testid="input-weekly-user-ids"
                value={manualUserIds}
                onChange={(event) => setManualUserIds(event.target.value)}
                placeholder="Comma or space separated list"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Leave blank to trigger for your admin account. Provide numeric
                IDs to target specific users.
              </p>
            </div>
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:gap-4 gap-2">
            <Button
              onClick={handleTriggerWeeklyCompetition}
              disabled={triggerWeeklyCompetitionMutation.isPending}
              data-testid="button-trigger-weekly-journey"
            >
              {triggerWeeklyCompetitionMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Triggering...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Trigger weekly journey
                </>
              )}
            </Button>
            <p className="text-sm text-muted-foreground">
              This emits `weekly_competition_start` and
              `competition_entry_created` journey events.
            </p>
          </div>
          {summary && (
            <Alert>
              <AlertDescription>
                {summary.message ||
                  `Triggered for ${summaryData?.emittedCount ?? 0} user(s).${
                    summaryData?.failedCount
                      ? ` ${summaryData.failedCount} failed.`
                      : ""
                  }`}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };

  // Journey Overview Cards Component
  const JourneyOverviewCards = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
      {journeyStats.map((journey) => {
        const IconComponent =
          JOURNEY_ICONS[journey.campaignType as keyof typeof JOURNEY_ICONS] ||
          Settings;
        const color =
          JOURNEY_COLORS[journey.campaignType as keyof typeof JOURNEY_COLORS] ||
          "#6B7280";
        const journeyName =
          JOURNEY_NAMES[journey.campaignType as keyof typeof JOURNEY_NAMES] ||
          journey.campaignName;

        return (
          <Card key={journey.campaignId} className="relative overflow-hidden">
            <div
              className="absolute top-0 left-0 w-full h-1"
              style={{ backgroundColor: color }}
            />
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: `${color}20`, color }}
                  >
                    <IconComponent className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">{journeyName}</h3>
                    <p className="text-xs text-gray-500">
                      {journey.campaignName}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant={
                      journey.campaignStatus === "active"
                        ? "default"
                        : "secondary"
                    }
                    className="text-xs"
                  >
                    {journey.campaignStatus}
                  </Badge>
                  <Switch
                    checked={journey.campaignStatus === "active"}
                    onCheckedChange={() =>
                      toggleCampaignMutation.mutate(journey.campaignId)
                    }
                    disabled={toggleCampaignMutation.isPending}
                    data-testid={`switch-campaign-${journey.campaignId}`}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold" style={{ color }}>
                    {journey.totalJourneys}
                  </div>
                  <div className="text-xs text-gray-500">Total Users</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {journey.activeJourneys}
                  </div>
                  <div className="text-xs text-gray-500">Active Now</div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Completed</span>
                  <span className="font-medium">
                    {journey.completedJourneys}
                  </span>
                </div>
                <Progress
                  value={
                    journey.totalJourneys > 0
                      ? (journey.completedJourneys / journey.totalJourneys) *
                        100
                      : 0
                  }
                  className="h-2"
                />
              </div>

              <div className="flex justify-between items-center text-xs text-gray-500 mt-3">
                <span>Paused: {journey.pausedJourneys}</span>
                <span>Cancelled: {journey.cancelledJourneys}</span>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  // Performance Overview Component
  const PerformanceOverview = () => (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Send className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">
                {performance?.totalSent.toLocaleString() || 0}
              </div>
              <div className="text-sm text-gray-500">Emails Sent</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">
                {performance?.deliveryRate.toFixed(1) || 0}%
              </div>
              <div className="text-sm text-gray-500">Delivery Rate</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-100 rounded-lg">
              <Eye className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-orange-600">
                {performance?.openRate.toFixed(1) || 0}%
              </div>
              <div className="text-sm text-gray-500">Open Rate</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Target className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600">
                {performance?.clickRate.toFixed(1) || 0}%
              </div>
              <div className="text-sm text-gray-500">Click Rate</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Journey Details Table Component
  const JourneyDetailsTable = () => (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Active User Journeys ({journeys.length})
            </CardTitle>
            <CardDescription>
              Monitor individual user progress through email campaigns
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                refetchJourneys();
                refetchAnalytics();
              }}
              disabled={journeysLoading || analyticsLoading}
              data-testid="button-refresh"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="min-w-[200px]">
            <Label htmlFor="journey-filter">Journey Type</Label>
            <Select value={selectedJourney} onValueChange={setSelectedJourney}>
              <SelectTrigger data-testid="select-journey-type">
                <SelectValue placeholder="All journeys" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Journeys</SelectItem>
                {campaigns.map((campaign: any) => (
                  <SelectItem key={campaign.id} value={campaign.id.toString()}>
                    {JOURNEY_NAMES[
                      campaign.triggerType as keyof typeof JOURNEY_NAMES
                    ] || campaign.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="min-w-[150px]">
            <Label htmlFor="state-filter">State</Label>
            <Select value={selectedState} onValueChange={setSelectedState}>
              <SelectTrigger data-testid="select-journey-state">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        {journeysLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading journeys...</p>
          </div>
        ) : journeys.length === 0 ? (
          <div className="text-center py-8">
            <Users className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No journeys found
            </h3>
            <p className="text-gray-500 mb-4">
              {selectedJourney !== "all" || selectedState !== "all"
                ? "No journeys match your current filters."
                : "User journeys will appear here when campaigns are triggered."}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Journey</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Next Step</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {journeys.map((journey: UserJourney) => (
                  <TableRow
                    key={journey.id}
                    data-testid={`journey-row-${journey.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                          <Users className="h-4 w-4 text-gray-500" />
                        </div>
                        <div>
                          <div className="font-medium text-sm">
                            {journey.userName || `User ${journey.userId}`}
                          </div>
                          <div className="text-xs text-gray-500">
                            {journey.userEmail}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium">
                          {journey.campaignName}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          journey.state === "active"
                            ? "default"
                            : journey.state === "completed"
                            ? "outline"
                            : journey.state === "paused"
                            ? "secondary"
                            : "destructive"
                        }
                        className="text-xs"
                      >
                        {journey.state}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">
                          Step {journey.currentStepIndex + 1}
                        </span>
                        {journey.state === "active" && (
                          <ArrowRight className="h-3 w-3 text-blue-500" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div>{formatDatePretty(journey.startedAt)}</div>
                        <div className="text-xs text-gray-500">
                          {formatDistanceToNow(new Date(journey.startedAt), {
                            addSuffix: true,
                          })}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {journey.nextStepAt ? (
                        <div className="text-sm">
                          <div>{formatDatePretty(journey.nextStepAt)}</div>
                          <div className="text-xs text-gray-500">
                            {formatDistanceToNow(new Date(journey.nextStepAt), {
                              addSuffix: true,
                            })}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            toast({
                              title: "Journey details",
                              description: "Feature coming soon",
                            });
                          }}
                          data-testid={`button-view-${journey.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteJourney(journey)}
                          disabled={deleteJourneyMutation.isPending}
                          data-testid={`button-delete-${journey.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-500">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.total
                  )}{" "}
                  of {pagination.total} journeys
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={pagination.page <= 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage((prev) =>
                        Math.min(prev + 1, pagination.totalPages)
                      )
                    }
                    disabled={pagination.page >= pagination.totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (analyticsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading CRM Journeys...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Bot className="h-8 w-8" />
              CRM User Journeys
            </h1>
            <p className="text-gray-600 mt-2">
              Monitor and manage automated email campaigns and user progression
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-sm">
              {journeyStats.length} Active Campaigns
            </Badge>
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export Data
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const response = await apiRequest(
                    "POST",
                    "/api/admin/crm/test-event",
                    {
                      eventType: "user_registered",
                      userId: 1, // Test with user ID 1
                      data: {
                        email: "test@example.com",
                        firstName: "Test",
                        lastName: "User",
                      },
                    }
                  );
                  const result = await response.json();
                  toast({
                    title: result.success ? "Success" : "Error",
                    description: result.message,
                    variant: result.success ? "default" : "destructive",
                  });
                  if (result.success) {
                    refetchJourneys();
                    refetchAnalytics();
                  }
                } catch (error) {
                  toast({
                    title: "Error",
                    description: "Failed to trigger test event",
                    variant: "destructive",
                  });
                }
              }}
            >
              <Zap className="h-4 w-4 mr-2" />
              Test Journey
            </Button>
          </div>
        </div>

        <ManualJourneyTools />

        {/* Performance Overview */}
        <PerformanceOverview />

        {/* Journey Overview Cards */}
        <JourneyOverviewCards />

        {/* Journey Details Table */}
        <JourneyDetailsTable />

        {/* Quick Stats Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              System Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  {journeyStats.reduce((acc, j) => acc + j.totalJourneys, 0)}
                </div>
                <p className="text-gray-600">Total Users in Journeys</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">
                  {journeyStats.reduce((acc, j) => acc + j.activeJourneys, 0)}
                </div>
                <p className="text-gray-600">Currently Active</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">
                  {journeyStats.reduce(
                    (acc, j) => acc + j.completedJourneys,
                    0
                  )}
                </div>
                <p className="text-gray-600">Successfully Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
